import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  listContacts,
  previewAccountScores,
  researchTopContacts,
  reviewContact,
} from '../api/client';
import { useScoringConfig } from '../scoring/ScoringConfigContext';
import './PeopleResearch.css';
import { ExternalLink, Search, Info } from 'lucide-react';

const PERSONAS = {
  buyer: 'Primary buyer',
  champion: 'Technical champion',
  executive: 'Executive decision-maker',
};

const EMAIL_STATUS_LABELS = {
  no_email: 'No work email found',
  unverified: 'Unverified',
  published: 'Published, not verified',
  verified: 'Valid work email',
  risky: 'Risky / uncertain',
  invalid: 'Invalid email',
};

const NON_PERSON_LABELS = new Set([
  'about us', 'contact us', 'leadership team', 'meet the team', 'our approach',
  'our impact', 'our mission', 'our platform', 'our products', 'our research',
  'our story', 'our values', 'our vision', 'our work', 'our company', 'our team',
  'team members', 'the team', 'what we build', 'what we do', 'what we make',
]);

function isReservedExampleDomain(value) {
  return typeof value === 'string' && value.trim().toLowerCase().endsWith('.example');
}

function isNonPersonPlaceholder(contact) {
  return NON_PERSON_LABELS.has((contact.full_name || '').trim().toLowerCase());
}

function domainUrl(domain) {
  if (!domain) return null;
  const normalized = domain.startsWith('http') ? domain : `https://${domain}`;
  try {
    return new URL(normalized).toString();
  } catch {
    return null;
  }
}

function scoreTierClass(tier) {
  return `people-tier people-tier-${(tier || 'C').toLowerCase()}`;
}

export default function PeopleResearch() {
  const { config } = useScoringConfig();
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [researching, setResearching] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [error, setError] = useState(null);
  const [researchError, setResearchError] = useState(null);
  const [researchSummary, setResearchSummary] = useState(null);
  const automaticRunAttempted = useRef(false);

  const loadRankedAccounts = useCallback(async () => {
    setLoading(true);
    setContactsLoaded(false);
    setError(null);
    try {
      const result = await previewAccountScores(config);
      const topAccounts = result.items.slice(0, 10);
      setAccounts(topAccounts);
      if (topAccounts.length) {
        setContacts(await listContacts(topAccounts.map((item) => item.account.id)));
      } else {
        setContacts([]);
      }
    } catch (e) {
      setError(e.message || "Couldn't load companies and contacts.");
    } finally {
      setLoading(false);
      setContactsLoaded(true);
    }
  }, [config]);

  useEffect(() => {
    loadRankedAccounts();
  }, [loadRankedAccounts]);

  const contactsByAccount = useMemo(() => {
    const grouped = new Map();
    contacts.filter((contact) => contact.email && contact.email_status === 'verified').forEach((contact) => {
      const bucket = grouped.get(contact.account_id) || [];
      bucket.push(contact);
      grouped.set(contact.account_id, bucket);
    });
    return grouped;
  }, [contacts]);

  const displayContacts = contacts.filter(
    (contact) => contact.email && contact.email_status === 'verified' && !isNonPersonPlaceholder(contact),
  );
  const demoAccountIds = useMemo(
    () => new Set(accounts.filter((item) => isReservedExampleDomain(item.account.domain)).map((item) => item.account.id)),
    [accounts],
  );
  const pendingCount = displayContacts.filter(
    (contact) => contact.research_status === 'pending_review' && !demoAccountIds.has(contact.account_id),
  ).length;
  const approvedCount = displayContacts.filter(
    (contact) => contact.research_status === 'approved' && !demoAccountIds.has(contact.account_id),
  ).length;
  const runContactResearch = useCallback(async () => {
    if (!accounts.length || researching) return;
    setResearching(true);
    setResearchError(null);
    setResearchSummary(null);
    try {
      const result = await researchTopContacts(accounts.map((item) => item.account.id));
      setResearchSummary(result);
      setContacts(await listContacts(accounts.map((item) => item.account.id)));
    } catch (e) {
      setResearchError(e.message || "Couldn't research public contact sources.");
    } finally {
      setResearching(false);
    }
  }, [accounts, researching]);

  useEffect(() => {
    if (loading || !contactsLoaded || error || researching || accounts.length === 0 || automaticRunAttempted.current) return;
    automaticRunAttempted.current = true;
    if (contacts.some((contact) => contact.source_type)) return;

    const dailyKey = 'menagtm-contact-research-auto-run';
    const day = 24 * 60 * 60 * 1000;
    try {
      const lastRun = Number(window.localStorage.getItem(dailyKey) || 0);
      if (lastRun && Date.now() - lastRun < day) return;
      window.localStorage.setItem(dailyKey, String(Date.now()));
    } catch {
      // Research can still run when browser storage is unavailable.
    }
    runContactResearch();
  }, [accounts, contacts, contactsLoaded, error, loading, researching, runContactResearch]);

  async function updateContactReview(contactId, status) {
    setReviewingId(contactId);
    setResearchError(null);
    try {
      const updated = await reviewContact(contactId, status);
      if (status === 'dismissed') {
        setContacts((current) => current.filter((contact) => contact.id !== contactId));
      } else {
        setContacts((current) => current.map((contact) => contact.id === contactId ? updated : contact));
      }
    } catch (e) {
      setResearchError(e.message || "Couldn't update the contact review.");
    } finally {
      setReviewingId(null);
    }
  }

  const sourceSummary = researchSummary
    ? `${researchSummary.people_found} verified contacts found; ${researchSummary.new_for_review} new contacts queued for review across ${researchSummary.accounts.length} companies. ${researchSummary.withheld_count || 0} candidates without a verified email were withheld.`
    : null;

  return (
    <main className="people-page">
      <section className="people-intro">
        <div>
          <div className="people-eyebrow">STAGE 3 · AUTOMATED CONTACT RESEARCH</div>
          <h2>Find the right people</h2>
          <p>People in the right roles at the top 10 companies, with a verified work email.</p>
        </div>
        <button
          className="people-refresh"
          type="button"
          onClick={runContactResearch}
          disabled={loading || researching || accounts.length === 0}
        >
          <Search size={16} aria-hidden="true" />{researching ? 'Finding and verifying…' : 'Find and verify contacts'}
        </button>
      </section>

      <section className="people-stats" aria-label="Contact research summary">
        <div className="people-stat">
          <span className="people-stat-value">{loading ? '—' : accounts.length}</span>
          <span className="people-stat-label">Companies in top 10</span>
        </div>
        <div className="people-stat">
          <span className="people-stat-value">{loading ? '—' : pendingCount}</span>
          <span className="people-stat-label">People awaiting review</span>
        </div>
        <div className="people-stat">
          <span className="people-stat-value">{loading ? '—' : approvedCount}</span>
          <span className="people-stat-label">Approved contacts</span>
        </div>
      </section>

      <details className="people-workflow-note">
        <summary><Info size={15} aria-hidden="true" />How Hunter credits are used</summary>
        Hunter's free account includes 50 credits a month. A company search costs up to 1 credit and
        rechecking an email 0.5, so a run across ten companies uses up to 40. No emails are sent.
      </details>

      {researchSummary && (
        <div className="people-run-summary" role="status">
          {sourceSummary} Hunter may return fewer than three relevant contacts with valid emails.
        </div>
      )}
      {researchError && <div className="people-toast-error" role="alert">{researchError}</div>}

      {error && (
        <div className="people-state people-error" role="alert">
          <div>{error}</div>
          <button type="button" onClick={loadRankedAccounts}>Try again</button>
        </div>
      )}
      {loading && !error && <div className="people-state">Loading company rankings…</div>}
      {!loading && !error && accounts.length === 0 && (
        <div className="people-state">No companies have enough signals to calculate a score yet.</div>
      )}

      {!error && accounts.length > 0 && (
        <section className="ranked-companies" aria-label="Top companies by score">
          {accounts.map((item, index) => {
            const account = item.account;
            const isDemoAccount = isReservedExampleDomain(account.domain);
            const accountContacts = isDemoAccount
              ? []
              : (contactsByAccount.get(account.id) || []).filter((contact) => !isNonPersonPlaceholder(contact));
            const website = isDemoAccount ? null : domainUrl(account.domain);
            const researchResult = researchSummary?.accounts.find((row) => row.account_id === account.id);
            return (
              <article className="ranked-company" key={account.id}>
                <header className="ranked-company-head">
                  <div className="ranked-company-main">
                    <div className="rank-number">{String(index + 1).padStart(2, '0')}</div>
                    <div className="ranked-company-name-block">
                      <div className="ranked-company-name-line">
                        <h3>{account.name}</h3>
                        <span className={scoreTierClass(item.tier)}>Tier {item.tier}</span>
                      </div>
                      <div className="ranked-company-meta">
                        {isDemoAccount ? <span className="demo-data-flag">Demo data · reserved .example domain</span> : website ? <a href={website} target="_blank" rel="noreferrer">{account.domain}</a> : <span>No domain on record</span>}
                        <span>{item.signal_count} signals</span>
                        {item.last_signal_type && <span>Latest signal: {item.last_signal_type}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="rank-score-wrap">
                    <span className="rank-score">{Math.round(item.score)}</span>
                    <span className="rank-score-caption">of 100</span>
                  </div>
                </header>

                <div className="rank-score-track" aria-label={`Score ${Math.round(item.score)} out of 100`}>
                  <span style={{ width: `${Math.max(0, Math.min(100, item.score))}%` }} />
                </div>

                <div className="role-search-section">
                  <div className="role-search-heading">
                    <div>
                      <strong>Research results</strong>
                      <span>Verified work emails only, up to three per company.</span>
                    </div>
                    <span className="contact-count">{accountContacts.length} valid found</span>
                  </div>

                  {researchResult && (
                    <div className="company-research-note">
                      Public pages checked: {researchResult.pages_checked}.
                      {researchResult.domain_source && ` Domain source: ${researchResult.domain_source}.`}
                      {researchResult.note && ` ${researchResult.note}`}
                    </div>
                  )}

                  {accountContacts.length > 0 ? (
                    <div className="automated-contacts">
                      {accountContacts.map((contact) => (
                        <div className="saved-contact automated-contact" key={contact.id}>
                          <div className="saved-contact-person">
                            <span className={`persona-tag persona-tag-${contact.persona_type || 'buyer'}`}>
                              {PERSONAS[contact.persona_type] || 'Contact'}
                            </span>
                            <div>
                              <strong>{contact.full_name}</strong>
                              <span className="saved-contact-title">{contact.title || 'Job title not available'}</span>
                              {contact.fit_reason && <span className="contact-fit-reason">{contact.fit_reason}</span>}
                            </div>
                          </div>
                          <div className="saved-contact-details">
                            {isDemoAccount || isReservedExampleDomain(contact.email?.split('@').pop()) ? (
                              <span className="demo-email-label">Synthetic demo record — not a real contact or email</span>
                            ) : contact.email ? (
                              <a href={`mailto:${contact.email}`}>{contact.email}</a>
                            ) : <span>No public work email found</span>}
                            <span className={`email-status email-status-${contact.email_status || 'unknown'}`}>
                              {EMAIL_STATUS_LABELS[contact.email_status] || 'Email status not set'}
                            </span>
                            {contact.source_type && <span className="contact-source-type">Source: {contact.source_type === 'github' ? 'public GitHub profile' : contact.source_type === 'hunter_domain_search' ? 'Hunter Domain Search' : 'company website'}</span>}
                            {contact.source_url && <a href={contact.source_url} target="_blank" rel="noreferrer">Evidence <ExternalLink size={13} aria-hidden="true" /></a>}
                            {contact.email_verification_provider && <span className="contact-source-type">Email check: {contact.email_verification_provider}</span>}
                            {contact.email_verification_status && <span className="contact-source-type">Hunter status: {contact.email_verification_status}{contact.email_verified_at ? ` · verified ${new Date(contact.email_verified_at).toLocaleDateString()}` : ''}</span>}
                            {contact.email_source_url && contact.email_source_url !== contact.source_url && <a href={contact.email_source_url} target="_blank" rel="noreferrer">Email source <ExternalLink size={13} aria-hidden="true" /></a>}
                          </div>
                          <div className="contact-review-actions">
                            {contact.fit_score != null && <span className="contact-fit-score">{contact.fit_score}% fit</span>}
                            {contact.research_status === 'pending_review' ? (
                              <>
                                <button type="button" onClick={() => updateContactReview(contact.id, 'approved')} disabled={reviewingId === contact.id}>Approve</button>
                                <button type="button" className="dismiss-contact" onClick={() => updateContactReview(contact.id, 'dismissed')} disabled={reviewingId === contact.id}>Dismiss</button>
                              </>
                            ) : (
                              <span className="approved-contact-status">Approved</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-contacts-state">
                      {researchResult ? 'No verified work emails were found for relevant people at this company. Unverified candidates are not shown.' : 'Run contact research to find and verify relevant people.'}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </section>
      )}
    </main>
  );
}
